import React, { useState } from 'react';
import { X, DollarSign, AlertCircle } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { formatCurrency } from '../../../utils/earningsCalculations';

/**
 * ChargeAdjustmentModal
 *
 * Modal for adjusting/overriding the client charge for a job instance.
 * Used to correct errors in original charge calculation (e.g., wrong charge type).
 * Creates an audit trail with reason for adjustment.
 */
export default function ChargeAdjustmentModal({
  instance,
  job,
  originalCharge,
  companyGstRate = 10,
  onClose,
  onAdjust,
  submitting = false
}) {
  const [adjustedAmount, setAdjustedAmount] = useState(
    instance?.charge_adjustment !== null && instance?.charge_adjustment !== undefined
      ? instance.charge_adjustment
      : originalCharge || 0
  );
  const [includeGst, setIncludeGst] = useState(instance?.charge_adjustment_include_gst || false);
  const [reason, setReason] = useState(instance?.charge_adjustment_reason || '');

  const calculateGst = (amount, shouldIncludeGst) => {
    if (!shouldIncludeGst) return 0;
    return parseFloat(amount || 0) * (companyGstRate / 100);
  };

  const gstAmount = calculateGst(adjustedAmount, includeGst);
  const totalWithGst = parseFloat(adjustedAmount || 0) + gstAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return;

    await onAdjust(parseFloat(adjustedAmount), reason, includeGst, gstAmount);
  };

  const difference = parseFloat(adjustedAmount) - (originalCharge || 0);
  const hasExistingAdjustment = instance?.charge_adjustment !== null && instance?.charge_adjustment !== undefined;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="bg-background w-full max-w-md rounded-xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Adjust Client Charge</h2>
              <p className="text-text-secondary text-sm">Override calculated charge amount</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Job Info */}
          <div className="mb-4 p-3 bg-surface rounded-lg">
            <p className="text-text-primary font-medium">{job?.job_name}</p>
            <p className="text-text-secondary text-sm">
              {instance?.contractor?.full_name || 'Unassigned'} • {instance?.scheduled_date}
            </p>
          </div>

          {/* Original Charge */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Calculated Charge (Original)
            </label>
            <div className="px-4 py-3 bg-surface border border-border rounded-lg text-text-tertiary">
              {formatCurrency(originalCharge)}
              <span className="text-xs text-text-tertiary ml-2">
                ({job?.client_charge_type === 'hourly' ? 'Hourly rate' : 'Fixed amount'})
              </span>
            </div>
          </div>

          {/* Adjusted Amount */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Adjusted Amount <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={adjustedAmount}
                onChange={(e) => setAdjustedAmount(e.target.value)}
                className="w-full pl-8 pr-4 py-3 bg-surface border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-primary"
                required
              />
            </div>
            {difference !== 0 && (
              <p className={cn(
                'mt-1 text-xs',
                difference > 0 ? 'text-green-400' : 'text-red-400'
              )}>
                {difference > 0 ? '+' : ''}{formatCurrency(difference)} from original
              </p>
            )}
          </div>

          {/* GST Toggle */}
          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeGst}
                onChange={(e) => setIncludeGst(e.target.checked)}
                className="w-4 h-4 rounded border-gray-600 bg-surface text-accent focus:ring-accent"
              />
              <span className="text-sm text-text-secondary">Add GST ({companyGstRate}%)</span>
              {includeGst && adjustedAmount && (
                <span className="text-sm text-text-tertiary">
                  (+{formatCurrency(gstAmount)})
                </span>
              )}
            </label>
            {includeGst && adjustedAmount && (
              <p className="mt-2 text-sm text-text-primary">
                Total with GST: <span className="font-medium">{formatCurrency(totalWithGst)}</span>
              </p>
            )}
          </div>

          {/* Reason */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Reason for Adjustment <span className="text-red-400">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Charge type was set to hourly instead of fixed..."
              rows={3}
              className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-primary resize-none"
              required
            />
          </div>

          {/* Warning */}
          <div className="mb-6 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-yellow-400">
              {hasExistingAdjustment
                ? 'This will update the existing charge adjustment. The change will be logged for audit purposes.'
                : 'This will override the calculated charge. The original amount and reason will be logged for audit purposes.'}
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!reason.trim() || submitting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              <DollarSign size={18} />
              {submitting ? 'Saving...' : hasExistingAdjustment ? 'Update Adjustment' : 'Apply Adjustment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
